// Shop taxonomy — THE definition of the Shop's structure, shared by the client
// router (src/data/shopRoutes.js re-exports it) and the Aura orchestrator.
//
// It lives here, in the edge-shared directory, for the same reason searchManifest.js
// does: the client can import across the repo boundary, an edge function cannot import
// from src/. One definition, two consumers — a second copy would drift and Aura would
// eventually advertise a route the router had renamed.
//
// Adding a department, category, subtype or wedding section here makes it routable on
// the site AND answerable by Aura in the same edit. New PRODUCTS need no change at all:
// every consumer queries the live products table.

// ── Departments (top-level worlds) ──────────────────────────────────────────
// type:'gender'  → filters by gender, second level = product CATEGORIES.
// type:'occasion'→ the Wedding world, second level = WEDDING_SECTIONS.
export const DEPARTMENTS = {
  women:   { label: 'Women',   type: 'gender',   gender: 'female', enabled: true },
  men:     { label: 'Men',     type: 'gender',   gender: 'male',   enabled: true },
  wedding: { label: 'Wedding', type: 'occasion',                   enabled: true },
  kids:    { label: 'Kids',    type: 'gender',   gender: 'kids',   enabled: false },
};

// Title-case a subtype/section token: 'small-leather-goods' -> 'Small Leather Goods'.
const titleCase = (s) => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const SUBTYPE_LABELS = { 'small-leather-goods': 'Small Leather Goods', 'candles-scent': 'Candles & Scent', 'soft-furnishings': 'Soft Furnishings' };
export const subtypeLabel = (t) => SUBTYPE_LABELS[t] || titleCase(t);

// ── Categories (second level under a gender department) ─────────────────────
// `category` = exact products.category value. `subtypes` = the route subtype
// tokens (match the products.subtype column from normalizeSubcategory), in nav
// order. 'other' is intentionally NOT a nav tab.
export const CATEGORIES = {
  clothing:    { label: 'Clothing',  category: 'Clothing',            departments: ['women', 'men'],
                 subtypes: ['dresses','tops','pants','skirts','knitwear','outerwear','jumpsuits','swimwear','lingerie','activewear'] },
  shoes:       { label: 'Shoes',     category: 'Shoes',               departments: ['women', 'men'],
                 subtypes: ['sneakers','boots','sandals','mules','flats','pumps','heels','slippers'],
                 sizes: ['35','36','37','38','39','40','41','42','43','44','45','UK 3','UK 4','UK 5','UK 6','UK 7','UK 8','UK 9'] },
  bags:        { label: 'Bags',      category: 'Bags',                departments: ['women', 'men'],
                 subtypes: ['totes','shoulder','crossbody','clutches','backpacks','small-leather-goods'] },
  jewellery:   { label: 'Jewellery', category: 'Jewelry And Watches', departments: ['women'],
                 subtypes: ['necklaces','earrings','rings','bracelets','watches'] },
  accessories: { label: 'Accessories', category: 'Accessories',       departments: ['women', 'men'],
                 subtypes: ['sunglasses','belts','hats','scarves','gloves','hair','hosiery'] },
  beauty:      { label: 'Beauty',    category: 'Beauty',              departments: ['women', 'men'],
                 subtypes: ['fragrance','skincare','makeup','haircare'] },
};

// ── Wedding sections (second level under the Wedding world) ──────────────────
// occasion tags (+ optional category), e.g. /shop/wedding/bridal-shoes.
export const WEDDING_SECTIONS = {
  'bridal':            { label: 'Bridal',             tags: ['bridal'] },
  'bridal-fashion':    { label: 'Bridal Fashion',     tags: ['bridal'], category: 'Clothing' },
  'bridal-shoes':      { label: 'Bridal Shoes',       tags: ['bridal'], category: 'Shoes' },
  'bridal-accessories':{ label: 'Bridal Accessories', tags: ['bridal'], category: 'Accessories' },
  'wedding-guest':     { label: 'Wedding Guest',      tags: ['wedding-guest'] },
  'bridesmaids':       { label: 'Bridesmaids',        tags: ['bridesmaid'] },
  'mother-of-the-bride':{ label: 'Mother of the Bride', tags: ['mother-of-bride','mother-of-groom'] },
  'groom':             { label: 'Groom',              tags: ['groom','groomsmen'] },
  'honeymoon':         { label: 'Honeymoon',          tags: ['honeymoon'] },
  'engagement':        { label: 'Engagement',         tags: ['engagement','proposal'] },
};

export function shopPath({ department, category, subtype } = {}) {
  if (department && category && subtype) return `/shop/${department}/${category}/${subtype}`;
  if (department && category) return `/shop/${department}/${category}`;
  if (department) return `/shop/${department}`;
  return '/shop';
}
